// Сколько живёт на экране системная строка "<имя> зашёл/вышел из чата"
// после её createdAt. Backend хранит такие сообщения дольше (TTL-индекс на
// expireAt, 10 минут, см. backend/.../ChatMessageModel.ts), но зрителю не
// нужно видеть их всё это время — на долгом эфире они бы копились в ленте.
export const SYSTEM_JOIN_LEAVE_VISIBLE_MS = 2 * 60 * 1000;

const JOIN_LEAVE_EVENT_TYPES = new Set(["USER_JOINED", "USER_LEFT"]);

export interface ChatMessageVisibilityFields {
  messageType: string;
  systemEventType?: string | null;
  userId?: string | null;
  createdAt: string | number | Date;
}

function isJoinLeaveMessage(message: ChatMessageVisibilityFields): boolean {
  return (
    message.messageType === "SYSTEM" &&
    JOIN_LEAVE_EVENT_TYPES.has(message.systemEventType ?? "")
  );
}

/**
 * Скрывает устаревшие join/left системные строки и схлопывает подряд идущие
 * дубликаты одного и того же пользователя (например, серию переподключений
 * до того, как сработал backend-дедуп) в одну. Обычные сообщения и
 * CONTENT_STARTED не трогает.
 *
 * `now` передаётся явно (а не берётся из Date.now() внутри) — вызывающий
 * компонент сам решает, когда пересчитывать видимость (периодический тик),
 * это же делает функцию чистой и тестируемой без фейковых таймеров.
 */
export function filterVisibleChatMessages<
  T extends ChatMessageVisibilityFields,
>(
  messages: T[],
  now: number,
  maxJoinLeaveAgeMs: number = SYSTEM_JOIN_LEAVE_VISIBLE_MS,
): T[] {
  const notExpired = messages.filter((message) => {
    if (!isJoinLeaveMessage(message)) return true;
    const createdAtMs = new Date(message.createdAt).getTime();
    return now - createdAtMs < maxJoinLeaveAgeMs;
  });

  const collapsed: T[] = [];
  for (const message of notExpired) {
    const previous = collapsed[collapsed.length - 1];
    const isDuplicateJoinLeave =
      previous !== undefined &&
      isJoinLeaveMessage(previous) &&
      isJoinLeaveMessage(message) &&
      previous.systemEventType === message.systemEventType &&
      String(previous.userId) === String(message.userId);

    if (isDuplicateJoinLeave) continue;
    collapsed.push(message);
  }

  return collapsed;
}
